

import { useEffect, useState } from "react";
import { CalendarGreyIcon, AlertIcon, CheckMarkTransparentIcon, RemoveIcon, EyeIcon, TelephoneIcon, MailGreyIcon, CheckGreenIcon, UsersBlackIcon } from "../../../assets/icons";
import ReservationEditView from "./ReservationEditView";
import ReservationInfoView from "./ReservationInfoView";
import { ReservationSettingsItemDTO } from "../../../models/reservation/reservation.response";
import { useNotification } from "../../toster-notification/NotificationContext";
import Loader from "../../shared/Loader";
import { useParams } from "react-router-dom";
import { reservationService } from "../../../services/reservationService";
import { handleErrorNotification } from "../../../utils/errorNotificatioMessage";
import { ReservationStatusType } from "../../../models/base/ReservationStatusType";
import { ReservationStatus } from "../../../utils/mappingEnums";
import { ToLocalDateString, ToLocalTimeString } from "../../../utils/dateFormater";
import UserModalTrigger from "../../shared/user-modal/UserModalTrigger";

const ReservationPage: React.FC = () => {

    const [isReservationInfoOpen, setIsReservationInfoOpen] = useState(false);
    const [isReservationEditOpen, setIsReservationEditOpen] = useState(false);

    const [reservations, setReservations] = useState<ReservationSettingsItemDTO[]>([]);
    const [reservationGroups, setReservationGroups] = useState<{ date: string; reservations: ReservationSettingsItemDTO[] }[]>([]);
    const [selectedReservationId, setSelectedReservationId] = useState<string>("");

    const [searchFilter, setSearchFilter] = useState<string>("");
    const [statusFilter, setStatusFilter] = useState<ReservationStatusType | "">("");
    const [dateFromFilter, setDateFromFilter] = useState<string>(new Date().toISOString().split('T')[0]);
    const [dateToFilter, setDateToFilter] = useState<string>(new Date(new Date().getTime() + 86400000).toISOString().split('T')[0]);

    const [loading, setLoading] = useState(false);
    const { notify } = useNotification();
    const { localId } = useParams<{ localId: string }>();


    useEffect(() => {
        fetchReservations();
    }, []);

    const fetchReservations = async () => {
        if (!localId) {
            notify("error", "ID lokala nije pronađen.");
            return;
        }

        setLoading(true);
        try {
            const response = await reservationService.getReservationByLocalId(localId);
            if (response.success && response.data) {
                setReservations(response.data);

                const grouped = response.data.reduce((groups, reservation) => {
                    const date = reservation.reservationDate.split('T')[0];

                    if (!groups[date]) {
                        groups[date] = [];
                    }

                    groups[date].push(reservation);
                    return groups;
                }, {} as { [key: string]: ReservationSettingsItemDTO[] });

                const groupedArray = Object.keys(grouped)
                    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
                    .map(date => ({
                        date,
                        reservations: grouped[date].sort((a, b) =>
                            a.reservationDate.localeCompare(b.reservationDate)
                        )
                    }));

                setReservationGroups(groupedArray);

            } else {
                notify("error", "Greška prilikom učitavanja rezervacija.");
            }
        } catch (error: any) {
            notify("error", "Greška prilikom učitavanja rezervacija.");
            handleErrorNotification(error, notify);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelReservation = async (reservationId: string) => {
        if (!localId) {
            notify("error", "ID lokala nije pronađen.");
            return;
        }

        setLoading(true);
        try {
            const response = await reservationService.changeStatus(localId, reservationId, ReservationStatusType.Canceled);
            if (response.success) {
                notify("success", "Rezervacija je uspešno otkazana.");
                fetchReservations();
            } else {
                notify("error", "Greška prilikom otkazivanja rezervacije.");
            }
        } catch (error: any) {
            notify("error", "Greška prilikom otkazivanja rezervacije.");
            handleErrorNotification(error, notify);
        } finally {
            setLoading(false);
        }
    };


    const reservationStatusClass = (status: ReservationStatusType): string => {
        switch (status) {
            case ReservationStatusType.Confirmed:
                return "green-bg";
            case ReservationStatusType.Canceled:
                return "red-bg";
            default:
                return "grey-bg";
        }
    };


    if (loading) {
        return <Loader />
    }


    return (
        <>
            {isReservationEditOpen &&
                <ReservationEditView
                    onClose={() => {
                        setIsReservationEditOpen(false)
                        fetchReservations();
                    }}
                    id={selectedReservationId}
                />
            }

            {isReservationInfoOpen &&
                <ReservationInfoView
                    onClose={() => {
                        setIsReservationInfoOpen(false);
                        fetchReservations();
                    }}
                    id={selectedReservationId}
                />
            }


            <section className="reservation-section">
                <div className="w-layout-blockcontainer container w-container">
                    <div className="sttings-content-wrapper">
                        <div className="header-div">
                            <div>
                                <h2 className="heading-3">Upravljanje Rezervacijama</h2>
                                <div className="text-block">
                                    Pregled i upravljanje rezervacijama stolova<br />
                                </div>
                            </div>
                        </div>
                        <div className="div-block-33">
                            <div className="admin-settings-box-div reservation-info">
                                <div>
                                    <div className="description-text fs-1rem">Danas</div>
                                    <div className="fs-1-5rem">{reservations.filter(r => r.reservationDate.split('T')[0] === new Date().toISOString().split('T')[0]).length}</div>
                                </div>
                                <img src={CalendarGreyIcon} loading="lazy" alt="" className="icon-size-28" />
                            </div>
                            <div className="admin-settings-box-div reservation-info">
                                <div>
                                    <div className="description-text fs-1rem">Na čekanju</div>
                                    <div className="fs-1-5rem">{reservations.filter(r => r.status === ReservationStatusType.Waiting).length}</div>
                                </div>
                                <img src={AlertIcon} loading="lazy" alt="" className="icon-size-28" />
                            </div>
                            <div className="admin-settings-box-div reservation-info">
                                <div>
                                    <div className="description-text fs-1rem">Nadolazeće</div>
                                    <div className="fs-1-5rem">{reservations.filter(r => new Date(r.reservationDate) > new Date() && r.status === ReservationStatusType.Confirmed).length}</div>
                                </div>
                                <img src={CheckMarkTransparentIcon} loading="lazy" alt="" className="icon-size-28" />
                            </div>
                        </div>
                        <div className="admin-settings-box-div">
                            <div className="fs-1rem">Filteri</div>
                            <div className="w-form">
                                <form className="form-8">
                                    <div className="filter-div">
                                        <div className="description-text">Pretraga</div>
                                        <input className="form-input w-input" placeholder="Pretraga po imenu korisnika, broju telefona, e-mail adresi..." type="text"
                                            value={searchFilter}
                                            onChange={(e) => setSearchFilter(e.target.value)}
                                        />
                                    </div>
                                    <div className="filter-div">
                                        <div className="description-text">Status</div>
                                        <select className="form-select auto-width w-select"
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value as ReservationStatusType | "")}
                                        >
                                            <option value={ReservationStatusType.Waiting}>Na čekanju</option>
                                            <option value={ReservationStatusType.Confirmed}>Potvrđena</option>
                                            <option value={ReservationStatusType.Canceled}>Otkazana</option>
                                            <option value={ReservationStatusType.Finished}>Završena</option>
                                            <option value={ReservationStatusType.DontShowUp}>Nije se pojavio</option>
                                        </select>
                                    </div>

                                    <div className="filter-div">
                                        <div className="description-text">Datum od</div>
                                        <input type="date" className="form-input grey w-input" maxLength={256}
                                            value={dateFromFilter}
                                            onChange={(e) => setDateFromFilter(e.target.value)}
                                        />
                                    </div>
                                    <div className="filter-div">
                                        <div className="description-text">Datum do</div>
                                        <input type="date" className="form-input grey w-input" maxLength={256}
                                            value={dateToFilter}
                                            onChange={(e) => setDateToFilter(e.target.value)}
                                        />
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div className="reservation-list-div">

                            {reservationGroups
                                .filter(group => {
                                    const groupDate = new Date(group.date);
                                    const fromDate = dateFromFilter ? new Date(dateFromFilter) : null;
                                    const toDate = dateToFilter ? new Date(dateToFilter) : null;
                                    if (fromDate && groupDate < fromDate) {
                                        return false;
                                    }
                                    if (toDate && groupDate > toDate) {
                                        return false;
                                    }
                                    return true;
                                })
                                .map(group => (
                                    <div key={group.date} className="admin-settings-box-div reservation">
                                        <div className="list-header">
                                            <div className="list-header-info">
                                                <div className="list-header-title">
                                                    {ToLocalDateString(group.date)}
                                                </div>
                                                <div className="list-header-description">
                                                    {group.reservations.length} {group.reservations.length === 1 ? 'rezervacija' : 'rezervacije'}<br />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="list-div reservation">
                                            <div className="list-row-div list-header-row reservation">
                                                <div>
                                                    <strong>Vreme</strong>
                                                </div>
                                                <div>
                                                    <strong>Gost</strong>
                                                </div>
                                                <div>
                                                    <strong>Kontakt</strong>
                                                </div>
                                                <div>
                                                    <strong>Osobe</strong>
                                                </div>
                                                <div>
                                                    <strong>Sto</strong>
                                                </div>
                                                <div>
                                                    <strong>Status</strong>
                                                </div>
                                                <div id="w-node-_99cdd64e-de92-a25b-a3a3-61f47c38ca25-1cee91cf">
                                                    <strong>Akcije</strong>
                                                </div>
                                            </div>

                                            {group.reservations
                                                .filter(reservation => {
                                                    const searchLower = searchFilter.toLowerCase();
                                                    return (
                                                        reservation.fullName.toLowerCase().includes(searchLower) ||
                                                        reservation.email.toLowerCase().includes(searchLower) ||
                                                        reservation.phoneNumber.toLowerCase().includes(searchLower) ||
                                                        reservation.id.toLowerCase().includes(searchLower)
                                                    );
                                                })
                                                .map(reservation => (
                                                    <div className="list-row-div reservation" key={reservation.id}>
                                                        <div>
                                                            <div className="bold-1rem">{ToLocalTimeString(reservation.reservationDate)}</div>
                                                            {reservation.departureDate &&
                                                                <div className="description-text">do {ToLocalTimeString(reservation.departureDate)}</div>
                                                            }
                                                        </div>
                                                        <div>
                                                            <UserModalTrigger
                                                                reservationId={reservation.id}
                                                                id={reservation.userId}
                                                                name={reservation.fullName}
                                                            />
                                                        </div>
                                                        <div className="div-block-36">
                                                            <div className="div-block-35">
                                                                <img src={TelephoneIcon} loading="lazy" alt="" className="icon-size-16" />
                                                                <div>{reservation.phoneNumber}</div>
                                                            </div>
                                                            <div className="div-block-35">
                                                                <img src={MailGreyIcon} loading="lazy" alt="" className="icon-size-16" />
                                                                <div>{reservation.email}</div>
                                                            </div>
                                                        </div>
                                                        <div className="div-block-35">
                                                            <img src={UsersBlackIcon} loading="lazy" alt="" className="icon-size-16" />
                                                            {reservation.numberOfPeople}
                                                        </div>
                                                        <div>Sto {reservation.tableNumber || "nije dodeljen"}</div>
                                                        <div className={`small-box-div ${reservationStatusClass(reservation.status)}`}>
                                                            {ReservationStatus(reservation.status)}
                                                        </div>
                                                        <div id="w-node-_705a5521-a46d-924a-59a3-14f80ffe5370-1cee91cf" className="table-row-action-btns-div">
                                                            {reservation.status === ReservationStatusType.Waiting &&
                                                                <>
                                                                    <img loading="lazy" src={CheckMarkTransparentIcon} alt="" className="category-list-div-row-actions-image" onClick={() => { setSelectedReservationId(reservation.id); setIsReservationEditOpen(true); }} />
                                                                    <img loading="lazy" src={RemoveIcon} alt="" className="category-list-div-row-actions-image" onClick={() => handleCancelReservation(reservation.id)} />
                                                                </>
                                                            }
                                                            <img loading="lazy" src={EyeIcon} alt="" className="category-list-div-row-actions-image" onClick={() => { setSelectedReservationId(reservation.id); setIsReservationInfoOpen(true); }} />
                                                        </div>
                                                    </div>
                                                ))}

                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default ReservationPage;